<template>
	<div>
		<b-breadcrumb>
			<b-breadcrumb-item :to="{ name: 'Services' }">Services</b-breadcrumb-item>
			<b-breadcrumb-item active v-if="!!error" class="text-danger"><i>Error</i></b-breadcrumb-item>
			<b-breadcrumb-item active v-else-if="loading>0" class="text-muted"><i>Loading...</i></b-breadcrumb-item>
			<b-breadcrumb-item active v-else>{{service&&service.name}}</b-breadcrumb-item>
		</b-breadcrumb>
		<b-alert :show="!!error" variant="danger">{{error}}</b-alert>
		<b-card-group v-if="!error&&service">
			<b-card no-body>
				<template #header>
					<div class="float-right">
						<b-button-group size="sm" style="margin: -10px">
							<b-dropdown v-if="authUrl" text="Auth" right variant="info">
								<b-dropdown-item @click.prevent="openAuthPopup(authUrl)" :href="authUrl">Authenticate</b-dropdown-item>
								<b-dropdown-item @click.prevent="$util.copyToClipboard(redirectUri)" :href="redirectUri">Copy Redirect URI</b-dropdown-item>
							</b-dropdown>
							<b-button @click="openEditModal(service.id)" v-b-tooltip.hover.left title="Edit service"><b-icon icon="pencil-fill"></b-icon></b-button>
							<b-button @click="openDeleteModal(service.id)" variant="danger" v-b-tooltip.hover.right.v-danger title="Delete service"><b-icon icon="trash"></b-icon></b-button>
						</b-button-group>
					</div>
					Details
				</template>
				<b-list-group flush>
					<b-list-group-item>Protocol name: <span class="float-right">{{service.protocol_name}}</span></b-list-group-item>
					<b-list-group-item>Name: <span class="float-right">{{service.name}}</span></b-list-group-item>
					<b-list-group-item v-for="(field, i) in fields" :key="i">
						{{field.label||i}}:
						<span class="float-right" v-if="field.type == 'bool'"><b-badge variant="success" v-if="service.settings[i]">true</b-badge><b-badge variant="danger" v-if="!service.settings[i]">false</b-badge></span>
						<span class="float-right" v-else-if="field.type == 'password'">******</span>
						<input class="float-right" readonly disabled v-else-if="field.type == 'color'" type="color" :value="service.settings[i]" :title="service.settings[i]">
						<span class="float-right" v-else>{{service.settings[i]}}</span>
					</b-list-group-item>
				</b-list-group>
			</b-card>
		</b-card-group>
		<b-card no-body v-if="browserLogin" class="mt-3">
			<template #header>
				<div class="float-right">
					<b-badge :variant="browserLoginVariant">{{browserLoginStatus ? browserLoginStatus.status : 'unknown'}}</b-badge>
				</div>
				Browser login
			</template>
			<b-card-body>
				<p class="text-muted mb-2">
					This platform only hands out a session cookie to a real browser. Starting a login opens
					<a :href="browserLogin.loginUrl" target="_blank" rel="noopener">{{browserLogin.loginUrl}}</a>
					in the remote browser<span v-if="browserLogin.automated"> and signs in with the credentials in the service settings</span>.
					Open the screen to watch it, or to finish a step it can't do by itself (captcha, 2FA).
					The cookie<span v-if="browserLogin.cookies.length > 1">s</span>
					<code>{{browserLogin.cookies.join(', ')}}</code>
					<span v-if="browserLogin.cookies.length > 1">are</span><span v-else>is</span> picked up automatically.
				</p>
				<b-alert :show="!!browserLoginError" variant="danger">{{browserLoginError}}</b-alert>
				<b-list-group flush v-if="browserLoginStatus">
					<b-list-group-item v-if="browserLoginStatus.message">{{browserLoginStatus.message}}</b-list-group-item>
					<b-list-group-item v-if="browserLoginStatus.currentUrl">Browser is on: <span class="float-right text-monospace text-truncate" style="max-width: 60%">{{browserLoginStatus.pageTitle}} — {{browserLoginStatus.currentUrl}}</span></b-list-group-item>
					<b-list-group-item v-if="browserLoginStatus.foundCookies && browserLoginStatus.foundCookies.length">Cookies seen: <span class="float-right">{{browserLoginStatus.foundCookies.join(', ')}}</span></b-list-group-item>
					<b-list-group-item v-if="browserLoginStatus.harvestedAt">Last harvested: <span class="float-right">{{new Date(browserLoginStatus.harvestedAt).toLocaleString()}}</span></b-list-group-item>
					<b-list-group-item v-if="browserLoginStatus.expiresAt && browserLoginBusyStatus">Gives up at: <span class="float-right">{{new Date(browserLoginStatus.expiresAt).toLocaleTimeString()}}</span></b-list-group-item>
					<b-list-group-item>Profile: <span class="float-right text-monospace">{{browserLoginStatus.profilePath}}</span></b-list-group-item>
				</b-list-group>
			</b-card-body>
			<template #footer>
				<b-button-group size="sm">
					<b-button variant="primary" :disabled="browserLoginWorking" @click="startBrowserLogin">
						<b-icon icon="box-arrow-in-right"></b-icon>
						{{browserLoginBusyStatus ? 'Restart login' : 'Start login'}}
					</b-button>
					<b-button variant="info" :disabled="browserLoginWorking" @click="openBrowserScreen">
						<b-icon icon="display"></b-icon> Open screen
					</b-button>
					<b-button variant="secondary" :disabled="browserLoginWorking || !browserLoginBusyStatus" @click="stopBrowserLogin">
						<b-icon icon="x"></b-icon> Cancel
					</b-button>
				</b-button-group>
			</template>
		</b-card>
		<b-card no-body v-if="service" class="mt-3">
			<template #header>
				<div class="float-right">
					<b-badge v-if="service.connectionStatus" :variant="service.connectionStatus.status == 'error' ? 'danger' : ''">{{service.connectionStatus.status}}</b-badge>
				</div>
				Connection status
			</template>
			<b-table small :items="connectionStatusLog" :fields="['status','message']" class="mb-0">
				<template #cell(status)="data">
					<b-badge v-if="data.item.status" :variant="data.item.status == 'error' ? 'danger' : ''">{{data.item.status}}</b-badge>
				</template>
			</b-table>
		</b-card>
		<service-edit-modal ref="edit-modal" @saved="loadService"></service-edit-modal>
		<service-delete-modal ref="delete-modal" @deleted="loadService"></service-delete-modal>
	</div>
</template>

<script>
import ServiceEditModal from '@/components/Service/Modal/Edit.vue';
import ServiceDeleteModal from '@/components/Service/Modal/Delete.vue';

export default {
	name: 'Service',
	components: {
		ServiceEditModal,
		ServiceDeleteModal,
	},
	data() { return {
		loading: 0,
		error: null,
		service: null,
		fields: null,
		connectionStatusLog: [],
		authUrl: null,
		redirectUri: null,
		browserLogin: null,        // protocol descriptor, if the bot has one
		browserLoginStatus: null,
		browserLoginError: null,
		browserLoginWorking: false,
		browserLoginTimer: null,
	}},
	computed: {
		// A login that is still under way — the states worth polling for.
		browserLoginBusyStatus() {
			return [ 'starting', 'logging-in', 'awaiting-login' ].includes(this.browserLoginStatus?.status);
		},
		browserLoginVariant() {
			switch(this.browserLoginStatus?.status) {
				case 'complete': return 'success';
				case 'failed':
				case 'expired': return 'danger';
				case 'starting':
				case 'logging-in':
				case 'awaiting-login': return 'warning';
				default: return 'secondary';
			}
		},
	},
	created() {
		this.loadService();
	},
	beforeDestroy() {
		clearTimeout(this.browserLoginTimer);
	},
	methods: {
		async loadService() {
			try {
				this.loading++;
				this.error = null;
				this.authUrl = null;
				this.redirectUri = null;
				this.service = await this.$api.get(`admin/service/${encodeURIComponent(this.$route.params.id)}`);
				this.authUrl = this.service.auth?.url;
				const proto = await this.$api.get(`admin/protocol/${encodeURIComponent(this.service.protocol_name)}`);
				this.fields = proto.serviceSettingFields;
				this.browserLogin = proto.browserLogin || null;
				if(this.browserLogin) this.loadBrowserLoginStatus();
			} catch(e) {
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		async loadBrowserLoginStatus() {
			clearTimeout(this.browserLoginTimer);
			try {
				this.browserLoginStatus = await this.$api.get(`admin/service/${encodeURIComponent(this.$route.params.id)}/browserLogin`);
			} catch(e) {
				this.browserLoginError = e.message || String(e);
				return;
			}
			// Keep refreshing only while something is actually happening.
			if(this.browserLoginBusyStatus)
				this.browserLoginTimer = setTimeout(() => this.loadBrowserLoginStatus(), 3000);
		},
		async startBrowserLogin() {
			this.browserLoginError = null;
			this.browserLoginWorking = true;
			try {
				this.browserLoginStatus = await this.$api.post(`admin/service/${encodeURIComponent(this.$route.params.id)}/browserLogin`, { force: true });
				await this.openBrowserScreen();
			} catch(e) {
				this.browserLoginError = e.message || String(e);
			} finally {
				this.browserLoginWorking = false;
				this.loadBrowserLoginStatus();
			}
		},
		async stopBrowserLogin() {
			this.browserLoginError = null;
			this.browserLoginWorking = true;
			try {
				this.browserLoginStatus = await this.$api.delete(`admin/service/${encodeURIComponent(this.$route.params.id)}/browserLogin`);
			} catch(e) {
				this.browserLoginError = e.message || String(e);
			} finally {
				this.browserLoginWorking = false;
			}
		},
		// The VNC screen is gated by a short-lived ticket cookie the API sets;
		// ask for one, then open the screen it points at.
		async openBrowserScreen() {
			try {
				const { url } = await this.$api.post('admin/browserVncTicket');
				window.open(`${process.env.VUE_APP_PIPIPUPUBASE||''}${url}`, 'remoteBrowser', 'width=1480,height=980,scrollbars=no,menubar=no,toolbar=no');
			} catch(e) {
				this.browserLoginError = e.message || String(e);
			}
		},
		openEditModal(id) {
			this.$refs['edit-modal'].show(id);
		},
		openDeleteModal(id) {
			this.$refs['delete-modal'].show(id);
		},
		openAuthPopup(authUrl) {
			window.open(authUrl, 'Authenticate', 'scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=600,height=500');
		},
	}
};
</script>
